import { Controller } from "stimulus"
import consumer from "channels/consumer"

// Mirror of `interactions--live` and `blasts--live` but for the Campaign live
// report. Subscribes to CampaignLiveChannel and surgically updates the
// counters + knob percentages + engagement-over-time chart so the page
// reflects sends/opens/clicks/bounces/complaints as `Notificate` rows are
// committed server-side (see Notificate#broadcast_to_campaign).
//
// The broadcast payload is `{ value, mode, scope }` where `scope` is
// "email" or "sms". Modes 33-36 are email-only events (opens/clicks/
// bounces/complaints). Mode 32 (Sent) can be either channel, so we rely on
// `scope` to pick the correct target.
export default class extends Controller {
  static targets = [
    "emailSends",
    "emailOpens",
    "emailClicks",
    "emailBounces",
    "emailComplaints",
    "emailOpenChart",
    "emailClickChart",
    "emailBounceChart",
    "emailComplaintChart",
    "smsSends"
  ]

  connect() {
    this.subscription = consumer.subscriptions.create(
      { channel: "CampaignLiveChannel", id: this.data.get("id") },
      {
        connected:    this._connected.bind(this),
        disconnected: this._disconnected.bind(this),
        received:     this._received.bind(this)
      }
    )
  }

  disconnect() {
    if (this.subscription) {
      consumer.subscriptions.remove(this.subscription)
    }
  }

  _connected() {}
  _disconnected() {}

  _received(data) {
    this._updateCounters(data)
    this._updateKnobCharts()
    this._refreshEngagementCharts()
  }

  _updateCounters(data) {
    const value = parseFloat(data.value)
    if (Number.isNaN(value)) return

    const scope = data.scope || "email"

    switch (parseInt(data.mode, 10)) {
      case 32:
        if (scope === "sms" && this.hasSmsSendsTarget) {
          this.smsSendsTarget.innerText = this._formatNumber(value)
        } else if (this.hasEmailSendsTarget) {
          this.emailSendsTarget.innerText = this._formatNumber(value)
        }
        break
      case 33:
        if (this.hasEmailOpensTarget) this.emailOpensTarget.innerText = this._formatNumber(value)
        break
      case 34:
        if (this.hasEmailClicksTarget) this.emailClicksTarget.innerText = this._formatNumber(value)
        break
      case 35:
        if (this.hasEmailBouncesTarget) this.emailBouncesTarget.innerText = this._formatNumber(value)
        break
      case 36:
        if (this.hasEmailComplaintsTarget) this.emailComplaintsTarget.innerText = this._formatNumber(value)
        break
    }
  }

  _updateKnobCharts() {
    const sends      = this._readCounter("hasEmailSendsTarget",      "emailSendsTarget")
    const opens      = this._readCounter("hasEmailOpensTarget",      "emailOpensTarget")
    const clicks     = this._readCounter("hasEmailClicksTarget",     "emailClicksTarget")
    const bounces    = this._readCounter("hasEmailBouncesTarget",    "emailBouncesTarget")
    const complaints = this._readCounter("hasEmailComplaintsTarget", "emailComplaintsTarget")

    this._setKnob("hasEmailOpenChartTarget",      "emailOpenChartTarget",      sends > 0 ? (opens   / sends)  * 100 : 0)
    this._setKnob("hasEmailClickChartTarget",     "emailClickChartTarget",     opens > 0 ? (clicks  / opens)  * 100 : 0)
    this._setKnob("hasEmailBounceChartTarget",    "emailBounceChartTarget",    sends > 0 ? (bounces / sends)  * 100 : 0)
    this._setKnob("hasEmailComplaintChartTarget", "emailComplaintChartTarget", sends > 0 ? (complaints / sends) * 100 : 0)
  }

  // Re-pull engagement segments and re-render the Chartist line charts so
  // the time-series visualizations stay in sync with the counters. The
  // controller#live_data endpoint returns { email: [...], sms: [...] }.
  _refreshEngagementCharts() {
    const accountId = document.querySelector("body").getAttribute("data-account-id")
    const timeframeEl = document.querySelector("#timeframe_current")
    const months = timeframeEl ? timeframeEl.getAttribute("data-active") : 0

    $.ajax({
      type: "GET",
      url: `/app/${accountId}/campaign/live_data/${this.data.get("id")}`,
      cache: false,
      data: { months: months },
      success: (res) => {
        this._renderEmailChart(res.email || [])
        this._renderSmsChart(res.sms || [])
      }
    })
  }

  _renderEmailChart(segments) {
    const container = document.querySelector("#email-engagement-over-time")
    if (!container || segments.length === 0) return

    const dates          = segments.map((v) => v.y)
    const sends_vals     = segments.map((v) => v.a)
    const opens_vals     = segments.map((v) => v.b)
    const clicks_vals    = segments.map((v) => v.c)
    const bounce_vals    = segments.map((v) => v.d)
    const complaint_vals = segments.map((v) => v.e)

    const build = (vals) => vals.map((v, idx) => {
      const label = dates[idx].includes("-") ? dates[idx] : `${dates[idx]}:00`
      const meta  = `${label}, Sends: ${sends_vals[idx]}, Opens: ${opens_vals[idx]}, Clicks: ${clicks_vals[idx]}, Bounces: ${bounce_vals[idx]}, Complaints: ${complaint_vals[idx]}`
      return { meta, value: v }
    })

    new Chartist.Line("#email-engagement-over-time", {
      labels: dates,
      series: [
        { data: build(sends_vals),     className: "series-a", name: "Sends" },
        { data: build(opens_vals),     className: "series-b", name: "Opens" },
        { data: build(clicks_vals),    className: "series-c", name: "Clicks" },
        { data: build(bounce_vals),    className: "series-d", name: "Bounces" },
        { data: build(complaint_vals), className: "series-e", name: "Complaints" }
      ]
    }, {
      low: 0,
      showArea: true,
      classNames: { line: "ct-line graph-line", area: "ct-area graph-area", point: "ct-point graph-point" },
      axisX: { showGrid: false },
      axisY: { labelInterpolationFnc: (value) => value % 1 === 0 ? value : null }
    })
  }

  _renderSmsChart(segments) {
    const container = document.querySelector("#sms-engagement-over-time")
    if (!container || segments.length === 0) return

    const dates      = segments.map((v) => v.y)
    const sends_vals = segments.map((v) => v.a)

    const sends = sends_vals.map((v, idx) => {
      const label = dates[idx].includes("-") ? dates[idx] : `${dates[idx]}:00`
      return { meta: `${label},Sends: ${v}`, value: v }
    })

    new Chartist.Line("#sms-engagement-over-time", {
      labels: dates,
      series: [{ data: sends, className: "series-a", name: "Sends" }]
    }, {
      low: 0,
      showArea: true,
      classNames: { line: "ct-line graph-line", area: "ct-area graph-area", point: "ct-point graph-point" },
      axisX: { showGrid: false },
      axisY: { labelInterpolationFnc: (value) => value % 1 === 0 ? value : null }
    })
  }

  _readCounter(hasTargetKey, targetKey) {
    if (!this[hasTargetKey]) return 0
    return this._parseValueToFloat(this[targetKey].innerText)
  }

  _setKnob(hasTargetKey, targetKey, ratio) {
    if (!this[hasTargetKey]) return
    jQuery(this[targetKey]).val(ratio.toFixed(2)).trigger("change")
  }

  _parseValueToFloat(value) {
    return parseFloat(String(value).replace(/,/g, "")) || 0
  }

  _formatNumber(value) {
    return value.toLocaleString("en-US")
  }
}
